import { useHead } from '@vueuse/head';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { BreadcrumbList, ListItem, WithContext } from 'schema-dts';

export interface BreadcrumbWithUrl {
  name: string;
  url: string,
  parent?: string | BreadcrumbWithUrl;
}

export interface Breadcrumb extends Omit<BreadcrumbWithUrl, 'url'> {
  url?: BreadcrumbWithUrl['url'];
}

const getParentCrumb = (crumb: Breadcrumb): BreadcrumbWithUrl | undefined => {
  if (!crumb.parent) return undefined;

  if (typeof crumb.parent === 'object') return crumb.parent;

  const router = useRouter();
  const route = router.resolve(crumb.parent);
  const parentCrumbs = route.meta.breadcrumbs;

  if (!parentCrumbs) return undefined;

  const url = window.location.origin + route.href;

  return {
    ...parentCrumbs,
    url,
  };
};

const buildBreadcrumbs = (root: Breadcrumb): WithContext<BreadcrumbList> => {
  const crumbs: ListItem[] = [{
    '@type': 'ListItem',
    name: root.name,
  }];
  const crumbUrls = new Set();

  let currentCrumb = getParentCrumb(root);
  while (currentCrumb) {
    if (crumbUrls.has(currentCrumb.url)) {
      // A url atual já foi processada. Isso indica uma referência cíclica, então encerramos mais
      // cedo para evitar um loop infinito.
      break;
    }

    crumbUrls.add(currentCrumb.url);

    crumbs.push({
      '@type': 'ListItem',
      name: currentCrumb.name,
      item: currentCrumb.url,
    });

    currentCrumb = getParentCrumb(currentCrumb);
  }

  const itemListElement = crumbs
    .reverse()
    .map((crumb, index) => ({
      ...crumb,
      position: index + 1,
    }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };
};

const useBreadcrumbs = (): void => {
  const route = useRoute();

  const root = computed(() => route.meta.breadcrumbs);

  const crumbs = computed<string | undefined>(() => {
    if (!root.value) return undefined;

    return JSON.stringify(buildBreadcrumbs(root.value));
  });

  useHead({
    script: [
      {
        key: 'breadcrumbs',
        type: 'application/ld+json',
        children: crumbs,
      },
    ],
  });
};

export default useBreadcrumbs;
